// UsersList.js
import React, { useEffect, useState, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const UsersList = () => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const pageSize = 5;
const didInitialFetch = useRef(false);

// -------------------------------------
// Shared fetch function
// -------------------------------------
const fetchUsers = async () => {
	if (!isAuthenticated) return;

	setLoading(true);

	try {
		// Acquire token
		let tokenResponse;
		try {
			tokenResponse = await instance.acquireTokenSilent({
				scopes: ["User.Read.All"],
				account: accounts[0],
			});
		} catch (err) {
			if (err.name === "InteractionRequiredAuthError") {
				tokenResponse = await instance.acquireTokenPopup({
					scopes: ["User.Read.All"],
				});
			} else throw err;
		}

		// Fetch users from API
		const res = await fetch(`${apiBaseUrl}/users`, {
			headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
		});

		if (res.status === 403) {
			setError("Access Denied. You must be admin to view this section.");
			setUsers([]);
		} else if (!res.ok) {
			setError("Failed to fetch users from server.");
			setUsers([]);
		} else {
			const data = await res.json();
			setUsers(data || []);
			setError("");

			// Cache locally for 24h
			localStorage.setItem(
				"usersListCache",
				JSON.stringify({ data: data || [], timestamp: Date.now() })
			);
		}
	} catch (err) {
		console.error("Error fetching users:", err);
		setError("Network error while fetching users.");
		setUsers([]);
	} finally {
		setLoading(false);
	}
};

// -------------------------------------
// StrictMode-safe lazy fetch with cache
// -------------------------------------
useEffect(() => {
	if (didInitialFetch.current) return; // Only run once

	const cached = JSON.parse(localStorage.getItem("usersListCache") || "null");
	const now = Date.now();
	const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h

	if (cached && now - cached.timestamp < CACHE_DURATION) {
		// Use cache immediately
		setUsers(cached.data || []);
		setLoading(false);
		return; // skip fetch
	}

	// Cache missing/expired -> lazy fetch after 5s
	const timer = setTimeout(() => {
		fetchUsers();
    	didInitialFetch.current = true;
	}, 5000);

	return () => clearTimeout(timer);
}, []);

// -------------------------------------
// Reactive fetch on accounts/auth changes (StrictMode-safe)
// -------------------------------------
const didReactiveFetch = useRef(false);

useEffect(() => {
	// Only run once, and only after initial mount
	if (!didInitialFetch.current || didReactiveFetch.current) return;
	if (!isAuthenticated) return;


	// Fetch after 5s delay to avoid blocking dashboard
	const timer = setTimeout(() => {
		fetchUsers();
    	didReactiveFetch.current = true;
	}, 5000);

	return () => clearTimeout(timer);
}, [accounts, instance, isAuthenticated]);



  if (loading) {
    return <p className="text-gray-500 text-sm animate-pulse">Loading users...</p>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center text-center p-4 bg-red-50 rounded-md border border-red-200">
        <h2 className="text-lg font-semibold text-red-600">Error</h2>
        <p className="text-sm text-gray-700 mt-1">{error}</p>
      </div>
    );
  }

  const filteredUsers = users.filter(
    (u) =>
      u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
      u.userPrincipalName?.toLowerCase().includes(search.toLowerCase()) ||
      u.role?.toLowerCase().includes(search.toLowerCase()) ||
      u.jobTitle?.toLowerCase().includes(search.toLowerCase())
  );

  const start = (page - 1) * pageSize;
  const pagedUsers = filteredUsers.slice(start, start + pageSize);
  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  return (
    <div className="w-full flex-1 overflow-y-auto">
      <div className="flex w-full items-center justify-between mb-5">
        <input
          type="text"
          placeholder="Search by name, email, role or job title..."
          className="flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring focus:ring-blue-200"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-100 text-sm md:text-base">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-center font-medium text-gray-700 w-12">#</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Email</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Role</th>
              <th className="px-3 py-2 text-center font-medium text-gray-700">Job Title</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pagedUsers.map((u, idx) => (
              <tr key={u.id} className="hover:bg-gray-300 even:bg-gray-200 odd:bg-white">
                <td className="px-3 py-2 text-gray-600 text-center">{start + idx + 1}</td>
                <td className="px-3 py-2 font-medium text-gray-800 truncate">{u.displayName}</td>
                <td className="px-3 py-4 text-gray-600 truncate text-center">{u.userPrincipalName}</td>
                <td className="px-3 py-4 text-gray-600 text-center">{u.role}</td>
                <td className="px-3 py-4 text-gray-600 text-center">{u.jobTitle ? u.jobTitle : "Not Found"}</td>
              </tr>
            ))}

            {pagedUsers.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-3 py-4 text-center text-gray-500 italic"
                >
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-3">
          <button
            className="px-3 py-1 text-sm rounded-md border disabled:opacity-50"
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
          >
            Prev
          </button>
          <span className="text-sm text-gray-700">
            Page {page} of {totalPages}
          </span>
          <button
            className="px-3 py-1 text-sm rounded-md border disabled:opacity-50"
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default UsersList;
